// @flow strict

import type {GraphStatus} from 'src/types/chatbot';


export const statusLabels: {[GraphStatus]: string} = {
  init: 'Draft',
  building: 'Building',
  success: 'Ready for use',
  failed: 'In manual review',
  archived: 'Archived',
};

function getChatbotStagingDomain(hostname) {
  const domains = hostname.split('.');
  const senseStagingDomain = domains[1];
  return {
    'stage-chatbot': 'stage-1',
    'stage-chatbot-02': 'stage-02',
    'stage-chatbot-03': 'stage-03',
    'stage-chatbot-04': 'stage-04',
    'stage-chatbot-05': 'stage-05',
    'stage-chatbot-06': 'stage-06',
  }[senseStagingDomain];
}

export const useChatbotApiPath = (env: {
  agencySlug: string,
  serverRuntimeEnvironment: string,
  hostname: string,
  chatbotApiUrl: string,
}): string => {
  let chatbot_url;

  const {agencySlug, serverRuntimeEnvironment, chatbotApiUrl, hostname} = env;

  switch (serverRuntimeEnvironment) {
    case 'preprod':
    case 'production':
    case 'development':
      // Extract country code and domain info from chatbotApiUrl
      // Example: api.sensehqchat.sensehq.eu -> agencySlug.sensehqchat.sensehq.eu/api
      // Example: api.sensehqchat.com -> agencySlug.sensehqchat.com/api
      const urlParts = chatbotApiUrl.split('.');
      if (urlParts.length >= 2) {
        // Remove 'api' prefix and join remaining parts
        urlParts.shift();
        chatbot_url = `https://${agencySlug}.${urlParts.join('.')}/api`;
      } else {
        // Fallback to default domain if URL parsing fails
        chatbot_url = `https://${agencySlug}.sensehqchat.com/api`;
      }
      break;
    case 'staging':
    default:
      chatbot_url = `https://${agencySlug}${getChatbotStagingDomain(
        hostname,
      )}.sensehqchat.com/api`;
      break;
  }

  return chatbot_url;
};

export function getChatbotSnippetURL(
  hostname: string,
  agencySlug: string,
  serverRuntimeEnvironment:
    | 'production'
    | 'staging'
    | 'preprod'
    | 'development',
  chatbotApiUrl: string,
): string {
  let chatbot_url;

  switch (serverRuntimeEnvironment) {
    case 'preprod':
    case 'production':
    case 'development':
      // Extract country code and domain info from chatbotApiUrl
      // Example: api.sensehqchat.sensehq.eu -> agencySlug.sensehqchat.sensehq.eu
      // Example: api.sensehqchat.com -> agencySlug.sensehqchat.com
      const urlParts = chatbotApiUrl.split('.');
      if (urlParts.length >= 2) {
        // Remove 'api' prefix and join remaining parts
        urlParts.shift();
        chatbot_url = `https://${agencySlug}.${urlParts.join('.')}/snippet.js`;
      } else {
        // Fallback to default domain if URL parsing fails
        chatbot_url = `https://${agencySlug}.sensehqchat.com/snippet.js`;
      }
      break;
    case 'staging':
    default:
      chatbot_url = `https://${agencySlug}${getChatbotStagingDomain(
        hostname,
      )}.sensehqchat.com/snippet.js`;
      break;
  }

  return chatbot_url;
}

export const useFAQServicePath = (env: {
  agencySlug: string,
  serverRuntimeEnvironment: string,
  apiUrl: string,
  hostname: string,
  chatbotApiUrl: string,
}): string => {
  const {serverRuntimeEnvironment, hostname, chatbotApiUrl} = env;
  const domains = hostname.split('.');
  const senseStagingDomain = domains[1];

  let faq_url;

  switch (serverRuntimeEnvironment) {
    case 'preprod':
      faq_url = `https://chatbot-faqs.sandbox-preprod.sensehq.com/api/faqs`;
      break;
    case 'staging':
      faq_url = `https://chatbot-faqs.${senseStagingDomain}.sensehq.co/api/faqs`;
      break;
    case 'production':
    case 'development':
    default:
      const urlParts = chatbotApiUrl.split('.');
      const extension = urlParts[urlParts.length - 1] === 'eu' ? 'eu' : 'co';
      faq_url = `https://chatbot-faqs.sensehq.${extension}/api/faqs`;
      break;
  }

  return faq_url;
};

export const useFAQV2ServicePath = (env: {
  agencySlug: string,
  serverRuntimeEnvironment: string,
  apiUrl: string,
  hostname: string,
  chatbotApiUrl: string,
}): string => {
  const {serverRuntimeEnvironment, hostname, chatbotApiUrl} = env;
  const domains = hostname.split('.');
  const senseStagingDomain = domains[1];

  let faq_url;

  switch (serverRuntimeEnvironment) {
    case 'preprod':
      faq_url = `https://chatbot-faqs.sandbox-preprod.sensehq.com/api/v2`;
      break;
    case 'staging':
      faq_url = `https://chatbot-faqs.${senseStagingDomain}.sensehq.co/api/v2`;
      break;
    case 'production':
    case 'development':
    default:
      const urlParts = chatbotApiUrl.split('.');
      const extension = urlParts[urlParts.length - 1] === 'eu' ? 'eu' : 'co';
      faq_url = `https://chatbot-faqs.sensehq.${extension}/api/v2`;
      break;
  }

  return faq_url;
};
